/**
 * Number / date formatting, always driven by the CURRENTLY SELECTED language.
 * Components never call these directly — they take the locale-bound versions
 * from `useI18n()` (see src/i18n/index.tsx).
 */
import { siteConfig } from '@/config/site.config'

export type Locale = 'en-US' | 'vi-VN'

/**
 * Prices are stored in ONE currency (siteConfig.currency) and formatted for the
 * reader's language. Selling in đồng instead? Change `currency` in
 * site.config.ts and enter the seed prices in đồng — nothing else moves.
 */
export function formatPrice(value: number, locale: Locale = 'en-US'): string {
  if (!Number.isFinite(value) || value <= 0) return locale === 'vi-VN' ? 'Giá liên hệ' : 'Price on request'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: siteConfig.currency,
    maximumFractionDigits: 0,
  }).format(value)
}

/** Compact form for tight spots: 24500 → "$24.5K" / "24,5K US$". */
export function formatPriceShort(value: number, locale: Locale = 'en-US'): string {
  if (!Number.isFinite(value) || value <= 0) return locale === 'vi-VN' ? 'Giá liên hệ' : 'On request'
  const compact = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: siteConfig.currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  })
  return compact.format(value)
}

export function formatNumber(value: number, locale: Locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(Number.isFinite(value) ? value : 0)
}

export function discountPercent(price: number, oldPrice: number): number {
  if (!oldPrice || oldPrice <= price) return 0
  return Math.round(((oldPrice - price) / oldPrice) * 100)
}

export function formatDate(iso: string, locale: Locale = 'en-US'): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
}

/** "3 days ago" / "3 ngày trước" — Intl does the wording for every language. */
export function timeAgo(iso: string, locale: Locale = 'en-US'): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const diffSec = Math.round((then - Date.now()) / 1000)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ]
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSec) >= seconds) return rtf.format(Math.round(diffSec / seconds), unit)
  }
  return rtf.format(0, 'minute')
}

export function normalizePhone(raw: string): string {
  return raw.replace(/[^\d+ ]/g, '')
}

export function isValidPhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, '')
  return digits.length >= 8 && digits.length <= 15
}

export function isValidEmail(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw.trim())
}

/** Initials for the avatar discs — the site ships no stock photos of people. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}
